/**
 * i18next configuration.
 *
 * **Two namespaces, and the split is the point.**
 *
 * `translation` is `src/shared/locales/{pl,en}.json` — a **hand-maintained verbatim copy** of
 * `move-with-me-frontend/move-with-me/locales/`. Never edit those two files here: the mobile repo
 * is upstream and the next re-copy overwrites anything added on this side (foundation ADR §6).
 *
 * `web` is `src/shared/locales/web/{pl,en}.json` — copy this app **authors**, for screens the
 * mobile app does not have and never will. It exists because the earlier rule ("a web-only key
 * lands in the mobile repo first") had no answer for a string that is meaningless on mobile: the
 * screen telling a client to go and download the mobile app cannot have its copy live in the
 * mobile app. That rule blocked two finished screens on a translation round that would have added
 * dead keys to a shipping binary.
 *
 * The separate folder is what keeps the copy rule intact — re-copying upstream overwrites the two
 * files at the top level and cannot touch `web/`. The `web:` prefix at every call site is
 * deliberate too: it marks a string as ours at a glance, and greps clean.
 *
 * Both upstream bundles are fetched, in parallel, as their own chunks rather than static imports:
 * `pl.json` carries 262 keys `en.json` does not, so `fallbackLng: 'en'` only works if the
 * English bundle is actually present, and 612 KB of JSON does not belong in the entry chunk.
 *
 * Two RN-only pieces do not port: the `intl-pluralrules` polyfill (Hermes lacks
 * `Intl.PluralRules` and silently resolves every count to `_other`; browsers have it), and
 * `compatibilityJSON: 'v4'`, which existed for the same polyfill.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['pl', 'en'] as const;
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Polish is the source language: `pl.json` is authored first and is the more complete file.
 * A trainer with an English browser therefore still starts in Polish and switches
 * explicitly, rather than landing on the derivative, partly-untranslated English.
 */
export const DEFAULT_LANGUAGE: LanguageCode = 'pl';
export const FALLBACK_LANGUAGE: LanguageCode = 'en';

const LANGUAGE_STORAGE_KEY = 'movebuddy.language';

/** Narrow an arbitrary stored string to a language we ship. */
export function parseLanguage(raw: string | null | undefined): LanguageCode | null {
  return SUPPORTED_LANGUAGES.includes(raw as LanguageCode) ? (raw as LanguageCode) : null;
}

/**
 * Which bundle a runtime language tag resolves to — the total version of `parseLanguage`.
 *
 * i18next reports whatever it resolved, which can be a REGIONAL tag (`pl-PL` on a Polish Windows),
 * and the two places that have to answer "which of our two is this" — the account screen's language
 * row and the exercise content language's fallback — must answer it identically. Matched by prefix,
 * with everything that is not Polish resolving to English, which is also the backend's own default.
 */
export function resolveLanguage(raw: string | null | undefined): LanguageCode {
  return (raw ?? '').startsWith('pl') ? 'pl' : 'en';
}

function readStoredLanguage(): LanguageCode | null {
  try {
    return parseLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** The namespace holding copy this app authors. Always written out at the call site: `web:…`. */
export const WEB_NAMESPACE = 'web';

interface Bundles {
  /** i18next types a language's resources as an open map of namespaces, so this has to be one. */
  [namespace: string]: object;
  translation: object;
  web: object;
}

async function loadTranslations(): Promise<Record<LanguageCode, Bundles>> {
  // The web bundles are two small objects, so they are bundled with the chunk rather than split
  // again — the reason the upstream files are lazy is that they weigh 612 KB, which these do not.
  const [pl, en, webPl, webEn] = await Promise.all([
    import('~/shared/locales/pl.json'),
    import('~/shared/locales/en.json'),
    import('~/shared/locales/web/pl.json'),
    import('~/shared/locales/web/en.json'),
  ]);
  return {
    pl: { translation: pl.default, web: webPl.default },
    en: { translation: en.default, web: webEn.default },
  };
}

export async function initI18n(): Promise<I18nInstance> {
  const resources = await loadTranslations();

  await i18next.use(initReactI18next).init({
    resources,
    lng: readStoredLanguage() ?? DEFAULT_LANGUAGE,
    fallbackLng: FALLBACK_LANGUAGE,
    ns: ['translation', WEB_NAMESPACE],
    // The default stays `translation`, so every key copied from mobile reads identically on both
    // surfaces and only this app's own copy carries a prefix.
    defaultNS: 'translation',
    interpolation: {
      // React escapes for us; double-escaping mangles Polish quotation marks.
      escapeValue: false,
    },
  });

  return i18next;
}

export async function changeLanguage(language: LanguageCode): Promise<void> {
  await i18next.changeLanguage(language);
  document.documentElement.lang = language;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // A trainer who cannot persist the choice still gets it for this session.
  }
}

export { i18next };
